// admin/src/utils/financeValidation.js - 금융설정 데이터 검증 유틸리티
/**
 * 금융설정 데이터 검증 및 안전성 보장
 * 이 유틸리티는 송금 시스템과 연동되는 중요한 금융 데이터의 무결성을 보장합니다.
 */

export class FinanceValidation {
  /**
   * 환율 데이터 검증
   * @param {Object} exchangeRates - 환율 데이터
   * @returns {Object} 검증 결과
   */
  static validateExchangeRates(exchangeRates) {
    const errors = [];
    const warnings = [];

    console.log('🔍 환율 검증 시작:', exchangeRates);

    // 필수 필드 검증
    const requiredFields = ['usdToKrw', 'usdToMmk', 'krwToMmk'];
    requiredFields.forEach(field => {
      if (exchangeRates[field] === undefined || exchangeRates[field] === null || exchangeRates[field] === '') {
        errors.push(`${field} 필드가 누락되었습니다.`);
      }
    });

    // 숫자 타입 검증
    Object.entries(exchangeRates).forEach(([key, value]) => {
      console.log(`🔍 ${key} 검증:`, { value, type: typeof value, parsed: parseFloat(value) });
      if (value !== undefined && value !== null && value !== '') {
        const numValue = parseFloat(value);
        if (isNaN(numValue) || numValue <= 0) {
          errors.push(`${key} 값이 유효하지 않습니다: ${value}`);
        }
      }
    });

    // 환율 범위 검증 (실제 시장 환율 범위)
    if (exchangeRates.usdToKrw) {
      const usdToKrw = parseFloat(exchangeRates.usdToKrw);
      if (usdToKrw < 1000 || usdToKrw > 2000) {
        warnings.push(`USD/KRW 환율이 비정상적입니다: ${usdToKrw}`);
      }
    }

    if (exchangeRates.usdToMmk) {
      const usdToMmk = parseFloat(exchangeRates.usdToMmk);
      if (usdToMmk < 1000 || usdToMmk > 5000) {
        warnings.push(`USD/MMK 환율이 비정상적입니다: ${usdToMmk}`);
      }
    }

    // KRW/MMK 자동 계산 검증
    if (exchangeRates.usdToKrw && exchangeRates.usdToMmk && exchangeRates.krwToMmk) {
      const calculatedKrwToMmk = parseFloat(exchangeRates.usdToMmk) / parseFloat(exchangeRates.usdToKrw);
      const actualKrwToMmk = parseFloat(exchangeRates.krwToMmk);
      const difference = Math.abs(calculatedKrwToMmk - actualKrwToMmk);
      
      if (difference > 0.01) { // 1% 이상 차이
        warnings.push(`KRW/MMK 환율이 자동 계산값과 다릅니다. 계산값: ${calculatedKrwToMmk.toFixed(4)}, 입력값: ${actualKrwToMmk}`);
      }
    }

    return {
      isValid: errors.length === 0,
      errors,
      warnings
    };
  }

  /**
   * 수수료 설정 검증
   * @param {Object} feeSettings - 수수료 설정
   * @returns {Object} 검증 결과
   */
  static validateFeeSettings(feeSettings) {
    const errors = [];
    const warnings = [];

    console.log('🔍 수수료 검증 시작:', feeSettings);

    // 필수: 송금 수수료 요율(%)
    if (
      feeSettings.remittanceFeeRatePercent === undefined ||
      feeSettings.remittanceFeeRatePercent === null ||
      feeSettings.remittanceFeeRatePercent === ''
    ) {
      errors.push('remittanceFeeRatePercent 필드가 누락되었습니다.');
    }

    // 숫자 타입 및 범위 검증
    Object.entries(feeSettings).forEach(([key, value]) => {
      console.log(`🔍 ${key} 검증:`, { value, type: typeof value, parsed: parseFloat(value) });
      if (value !== undefined && value !== null && value !== '') {
        const numValue = parseFloat(value);
        if (isNaN(numValue) || numValue < 0) {
          errors.push(`${key} 값이 유효하지 않습니다: ${value}`);
        }
      }
    });

    const rate = parseFloat(feeSettings.remittanceFeeRatePercent);
    if (Number.isFinite(rate) && rate > 20) {
      errors.push('송금 수수료 요율은 20% 이하여야 합니다.');
    }

    if (Number.isFinite(rate) && (rate < 0.1 || rate > 10)) {
      warnings.push(`송금 수수료 요율이 비정상적일 수 있습니다: ${rate}%`);
    }


    return {
      isValid: errors.length === 0,
      errors,
      warnings
    };
  }

  /**
   * 전체 금융설정 검증
   * @param {Object} financeConfig - 전체 금융설정
   * @returns {Object} 검증 결과
   */
  static validateFinanceConfig(financeConfig) {
    const exchangeValidation = this.validateExchangeRates(financeConfig.exchangeRates || {});
    const feeValidation = this.validateFeeSettings(financeConfig.feeSettings || {});

    return {
      isValid: exchangeValidation.isValid && feeValidation.isValid,
      exchangeRates: exchangeValidation,
      feeSettings: feeValidation,
      allErrors: [...exchangeValidation.errors, ...feeValidation.errors],
      allWarnings: [...exchangeValidation.warnings, ...feeValidation.warnings]
    };
  }

  /**
   * 송금 시스템 연동 데이터 생성
   * @param {Object} financeConfig - 금융설정
   * @returns {Object} 송금 시스템용 데이터
   */
  static generateRemittanceData(financeConfig) {
    const validation = this.validateFinanceConfig(financeConfig);
    
    if (!validation.isValid) {
      throw new Error(`금융설정 검증 실패: ${validation.allErrors.join(', ')}`);
    }

    const { exchangeRates, feeSettings } = financeConfig;
    const rate = parseFloat(feeSettings.remittanceFeeRatePercent ?? 2.5);

    return {
      // 송금 시스템에서 사용할 환율 정보
      exchangeRates: {
        usdToKrw: parseFloat(exchangeRates.usdToKrw),
        usdToMmk: parseFloat(exchangeRates.usdToMmk),
        krwToMmk: parseFloat(exchangeRates.krwToMmk)
      },
      
      // 송금 시스템에서 사용할 수수료 정보
      fees: {
        remittanceFeeRatePercent: rate,
        sampleFeeOn100k: Math.round(100000 * (rate / 100)),
        kairosSharePercent: parseFloat(feeSettings.kairosSharePercent ?? 1.5),
        hectoSharePercent: parseFloat(feeSettings.hectoSharePercent ?? 1.0),
        hectoFeePerTransactionKRW: parseFloat(feeSettings.hectoFeePerTransactionKRW ?? 3000),
        minimumAmount: parseFloat(feeSettings.minimumAmount),
        maximumAmount: parseFloat(feeSettings.maximumAmount)
      },
      
      // UAB 쉐어 정보
      uabShare: {
        type: feeSettings.uabShareType || 'profit_share',
        profitShareRatio: parseFloat(feeSettings.uabProfitShareRatio || 50),
        fixedAmountPerDollar: parseFloat(feeSettings.uabFixedAmountPerDollar || 200)
      },
      
      // 검증 정보
      validation: {
        isValid: true,
        validatedAt: new Date().toISOString(),
        warnings: validation.allWarnings
      }
    };
  }

  /**
   * 금융설정 저장 전 최종 검증
   * @param {Object} financeConfig - 저장할 금융설정
   * @returns {Promise<Object>} 검증 결과
   */
  static async validateBeforeSave(financeConfig) {
    try {
      console.log('🔍 validateBeforeSave 시작:', financeConfig);
      
      const validation = this.validateFinanceConfig(financeConfig);
      console.log('🔍 validateFinanceConfig 결과:', validation);
      
      if (!validation.isValid) {
        console.error('❌ 검증 실패 - 상세 정보:');
        console.error('❌ 전체 오류들:', validation.allErrors);
        console.error('❌ 전체 오류들 (배열 내용):', JSON.stringify(validation.allErrors, null, 2));
        console.error('❌ 환율 검증:', validation.exchangeRates);
        console.error('❌ 환율 검증 (상세):', JSON.stringify(validation.exchangeRates, null, 2));
        console.error('❌ 수수료 검증:', validation.feeSettings);
        console.error('❌ 수수료 검증 (상세):', JSON.stringify(validation.feeSettings, null, 2));
        
        return {
          success: false,
          message: '금융설정 검증 실패',
          errors: validation.allErrors,
          warnings: validation.allWarnings,
          exchangeRates: validation.exchangeRates,
          feeSettings: validation.feeSettings
        };
      }

      // 송금 시스템 연동 데이터 생성 테스트
      console.log('🔍 송금 시스템 연동 데이터 생성 중...');
      const remittanceData = this.generateRemittanceData(financeConfig);
      console.log('🔍 송금 시스템 연동 데이터 생성 완료:', remittanceData);
      
      return {
        success: true,
        message: '금융설정 검증 성공',
        remittanceData,
        warnings: validation.allWarnings
      };
    } catch (error) {
      console.error('❌ validateBeforeSave 오류:', error);
      return {
        success: false,
        message: `금융설정 검증 중 오류 발생: ${error.message}`,
        errors: [error.message]
      };
    }
  }
}

export default FinanceValidation;
































